import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, CheckCircle2, ArrowLeft, Home, IndianRupee, Building2 } from "lucide-react";
import api, { imageUrl } from "../lib/api";
import { formatINR, typeLabel } from "../lib/constants";
import PropertyCard from "../components/PropertyCard";
import { EnquiryForm } from "../components/EnquiryForm";
import Seo from "../components/Seo";

const LocalityGuide = () => {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setD(null); setNotFound(false);
    api.get(`/localities/${slug}`).then((r) => setD(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-5 py-32 text-center">
      <h1 className="font-heading text-3xl text-stone-900">Locality not found</h1>
      <Link to="/localities" className="inline-block mt-6 rounded-full bg-brand text-white px-6 py-3 text-sm font-semibold">All localities</Link>
    </div>
  );
  if (!d) return <div className="max-w-7xl mx-auto px-5 py-20"><div className="h-96 rounded-2xl bg-stone-100 animate-pulse" /></div>;

  const mapQuery = encodeURIComponent(`${d.name}, ${d.city}, Rajasthan, India`);
  const stats = [
    { icon: Home, label: "Listings", value: d.count },
    { icon: IndianRupee, label: "Starting from", value: formatINR(d.min_price) },
    { icon: Building2, label: "Up to", value: formatINR(d.max_price) },
  ];

  return (
    <div>
      <Seo
        title={`Property in ${d.name}, ${d.city} — Prices, Types & Area Guide | Satyam Real Estate`}
        description={`${d.headline || `Explore property in ${d.name}, ${d.city}.`} ${d.count} listings from ${formatINR(d.min_price)}. ${(d.overview || "").slice(0, 120)}`}
        image={imageUrl(d.image)}
        jsonLd={{ "@context": "https://schema.org", "@type": "Place", name: `${d.name}, ${d.city}`, description: d.overview }}
      />

      {/* Hero */}
      <section className="relative h-[42vh] min-h-[320px] flex items-end">
        {d.image ? <img src={imageUrl(d.image)} alt={d.name} className="absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0 bg-stone-900" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full pb-10">
          <Link to="/localities" className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-4"><ArrowLeft size={16} /> All localities</Link>
          <p className="flex items-center gap-1.5 text-white/80 text-sm"><MapPin size={15} /> {d.city}, Rajasthan</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-white mt-1">{d.name}</h1>
          {d.headline && <p className="text-white/90 text-lg mt-2 max-w-2xl">{d.headline}</p>}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-[1fr_340px] gap-12">
        <div>
          <div className="grid grid-cols-3 gap-4" data-testid="locality-stats">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-stone-200 p-4">
                <s.icon className="text-brand mb-2" size={20} />
                <p className="text-xs text-stone-500">{s.label}</p>
                <p className="font-semibold text-stone-800">{s.value}</p>
              </div>
            ))}
          </div>

          {d.overview && (
            <div className="mt-10">
              <h2 className="font-heading text-2xl text-stone-900 mb-3">About {d.name}</h2>
              <p className="text-stone-600 leading-relaxed whitespace-pre-line">{d.overview}</p>
            </div>
          )}

          {d.highlights?.length > 0 && (
            <div className="mt-8">
              <h3 className="font-heading text-xl text-stone-900 mb-3">What to expect</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {d.highlights.map((h) => (
                  <div key={h} className="flex items-start gap-2 text-sm text-stone-700">
                    <CheckCircle2 size={17} className="text-brand mt-0.5 shrink-0" /> {h}
                  </div>
                ))}
              </div>
            </div>
          )}

          {d.types?.length > 0 && (
            <div className="mt-8">
              <h3 className="font-heading text-xl text-stone-900 mb-3">Available property types</h3>
              <div className="flex flex-wrap gap-2">
                {d.types.map((t) => (
                  <Link key={t} to={`/properties?locality=${encodeURIComponent(d.name)}&property_type=${t}`}
                    className="rounded-full border border-stone-200 text-stone-700 text-sm px-4 py-1.5 hover:border-brand transition-colors">
                    {typeLabel(t)}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Map */}
          <div className="mt-10">
            <h3 className="font-heading text-xl text-stone-900 mb-3">Location on map</h3>
            <div className="rounded-2xl overflow-hidden border border-stone-200" data-testid="locality-map">
              <iframe title={`Map of ${d.name}`} width="100%" height="360" loading="lazy" style={{ border: 0 }}
                src={`https://maps.google.com/maps?q=${mapQuery}&z=14&output=embed`} />
            </div>
          </div>

          {d.properties?.length > 0 && (
            <div className="mt-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl text-stone-900">Properties in {d.name}</h2>
                <Link to={`/properties?locality=${encodeURIComponent(d.name)}`} className="text-sm font-semibold text-brand">View all</Link>
              </div>
              <div className="grid gap-6 sm:grid-cols-2">
                {d.properties.map((p) => <PropertyCard key={p._id} p={p} />)}
              </div>
            </div>
          )}
        </div>

        <aside>
          <div className="sticky top-24 bg-white rounded-2xl border border-stone-200 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <h3 className="font-heading text-xl text-stone-900 mb-1">Interested in {d.name}?</h3>
            <p className="text-sm text-stone-500 mb-5">Tell us your budget and requirement — we'll find the right match.</p>
            <EnquiryForm locality={d.name} />
          </div>
        </aside>
      </div>
    </div>
  );
};

export default LocalityGuide;
